import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { IconHome, IconFolder, IconShoppingCart, IconChevronDown, IconChevronRight, IconSettings, IconCreditCard, IconLayout2, IconChartBar } from '@tabler/icons-react'

// Menu item interface
export interface PortalMenuItem {
  id: string
  label: string
  icon: React.ComponentType<{ size?: number | string; className?: string }>
  path?: string
  children?: PortalMenuItem[]
}

// Menu structure definition
export const portalMenuItems: PortalMenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: IconHome,
    path: '/portal/dashboard',
  },
  {
    id: 'projects',
    label: 'Projekte',
    icon: IconFolder,
    path: '/portal/projects',
  },
  {
    id: 'portfolios',
    label: 'Portfolios',
    icon: IconLayout2,
    path: '/portal/portfolios',
  },
  {
    id: 'statistics',
    label: 'Statistiken',
    icon: IconChartBar,
    path: '/portal/statistiken',
  },
  {
    id: 'shop',
    label: 'Shop',
    icon: IconShoppingCart,
    path: '/portal/shop',
  },
  {
    id: 'subscription',
    label: 'Abo & Rechnung',
    icon: IconCreditCard,
    path: '/portal/subscription',
  },
  {
    id: 'settings',
    label: 'Einstellungen',
    icon: IconSettings,
    path: '/portal/settings',
  },
]

interface PortalSidebarProps {
  isPinned?: boolean
  onPinnedChange?: (pinned: boolean) => void
  onNavigate?: () => void
  forceExpanded?: boolean
}

export default function PortalSidebar({ isPinned = false, onPinnedChange, onNavigate, forceExpanded }: PortalSidebarProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const location = useLocation()

  const isExpanded = forceExpanded || isPinned || isHovered

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }

  const isActive = (path?: string) => {
    if (!path) return false
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  return (
    <div
      className={`
        h-screen bg-[var(--portal-sidebar-bg)] border-r border-[var(--portal-border)]
        transition-all duration-300 ease-in-out
        flex flex-col
        overflow-hidden
        ${isExpanded ? 'w-64' : 'w-16'}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo / Header */}
      <div className="h-16 flex items-center justify-center">
        {isExpanded ? (
          <Link to={'/portal/dashboard'}>
            <span className="text-xl font-semibold whitespace-nowrap font-serif">Pic<span className='text-primary'>Field</span></span>
          </Link>
        ) : (
          <span className="text-xl font-semibold font-serif">PF</span>
        )}
      </div>

      {/* Menu Items */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {portalMenuItems.map(item => (
          <MenuItem
            key={item.id}
            item={item}
            isExpanded={isExpanded}
            isItemExpanded={expandedItems.has(item.id)}
            onToggleExpand={() => toggleExpanded(item.id)}
            isActive={isActive}
            onNavigate={onNavigate}
            level={0}
          />
        ))}
      </nav>

      {/* Pin Toggle (nur sichtbar wenn expanded) */}
      {isExpanded && onPinnedChange && (
        <div className="p-4 border-t border-[var(--portal-border)]">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPinned}
              onChange={(e) => onPinnedChange(e.target.checked)}
              className="checkbox checkbox-primary checkbox-sm"
            />
            <span className="text-sm">Fixiert</span>
          </label>
        </div>
      )}
    </div>
  )
}

interface MenuItemProps {
  item: PortalMenuItem
  isExpanded: boolean
  isItemExpanded: boolean
  onToggleExpand: () => void
  isActive: (path?: string) => boolean
  onNavigate?: () => void
  level: number
}

function MenuItem({ item, isExpanded, isItemExpanded, onToggleExpand, isActive, onNavigate, level }: MenuItemProps) {
  const hasChildren = item.children && item.children.length > 0
  const Icon = item.icon
  const active = isActive(item.path)
  const paddingLeft = level * 12

  // mx-2 (8px) on active items shifts content, so reduce paddingLeft to keep icons aligned
  const basePl = isExpanded ? 16 + paddingLeft : 0
  const effectivePl = active ? Math.max(basePl - 8, 0) : basePl
  const content = (
    <div
      className={`
        flex items-center gap-3 py-3
        transition-colors duration-200
        ${active
          ? 'bg-neutral rounded-lg mx-2'
          : 'opacity-50 hover:opacity-100 hover:[&>.border-indicator]:border-primary'}
        ${!isExpanded ? 'justify-center' : ''}
      `}
      style={{ paddingLeft: isExpanded ? `${effectivePl}px` : undefined, paddingRight: isExpanded ? '16px' : undefined }}
    >
      {isExpanded && <div className={`border-indicator self-center border-l-[3px] py-2 ${active ? 'border-primary' : 'border-transparent'}`} />}
      <Icon size={20} className="flex-shrink-0" />
      {isExpanded && (
        <>
          <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
            {item.label}
          </span>
          {hasChildren && (
            <span className="flex-shrink-0">
              {isItemExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            </span>
          )}
        </>
      )}
    </div>
  )

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={onToggleExpand}
          className="w-full text-left"
        >
          {content}
        </button>
        {isExpanded && isItemExpanded && (
          <div className="mt-1">
            {item.children!.map(child => (
              <MenuItem
                key={child.id}
                item={child}
                isExpanded={isExpanded}
                isItemExpanded={false}
                onToggleExpand={() => {}}
                isActive={isActive}
                onNavigate={onNavigate}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (item.path) {
    return (
      <Link to={item.path} onClick={onNavigate}>
        {content}
      </Link>
    )
  }

  return content
}
